import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getConversations, 
  getConversation, 
  sendMessage, 
  markConversationAsRead,
  getMessageableUsers,
  getUnreadMessageCount 
} from '../services/messages';
import './Messages.css';

export default function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageableUsers, setMessageableUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const recipientDropdownRef = useRef(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const [convs, users, count] = await Promise.all([
        getConversations(),
        getMessageableUsers(),
        getUnreadMessageCount(),
      ]);
      setConversations(convs);
      setMessageableUsers(users || []);
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setMessageableUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  const loadConversationMessages = useCallback(async (otherUserId) => {
    try {
      const msgs = await getConversation(otherUserId);
      setMessages(msgs);
      await markConversationAsRead(otherUserId);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadConversationMessages(selectedConversation.userId);
      const interval = setInterval(() => {
        loadConversationMessages(selectedConversation.userId);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, loadConversationMessages]);

  useEffect(() => {
    const prev = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;
    if (messages.length > prev) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const onOutside = (e) => {
      if (recipientDropdownRef.current && !recipientDropdownRef.current.contains(e.target)) {
        setRecipientDropdownOpen(false);
      }
    };
    if (recipientDropdownOpen) {
      document.addEventListener('mousedown', onOutside);
      return () => document.removeEventListener('mousedown', onOutside);
    }
  }, [recipientDropdownOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const recipientId = selectedConversation?.userId || selectedRecipient?.id;
    if (!recipientId) return;

    try {
      setSending(true);
      await sendMessage(recipientId, newMessage.trim());
      setNewMessage('');
      
      // Reload conversation
      if (selectedConversation) {
        await loadConversationMessages(recipientId);
      } else {
        // If starting new conversation, load it
        await loadConversationMessages(recipientId);
        const conv = conversations.find(c => c.userId === recipientId) || {
          userId: recipientId,
          user: selectedRecipient,
          lastMessage: null,
          unreadCount: 0,
        };
        setSelectedConversation(conv);
        setShowNewMessage(false);
        setSelectedRecipient(null);
      }
      
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleStartNewConversation = (recipient) => {
    setSelectedRecipient(recipient);
    setSelectedConversation(null);
    setMessages([]);
    setShowNewMessage(true);
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowNewMessage(false);
    setSelectedRecipient(null);
  };

  const handleCancelNewMessage = () => {
    setShowNewMessage(false);
    setSelectedRecipient(null);
    setRecipientDropdownOpen(false);
  };

  const getDisplayName = (userObj) => {
    if (!userObj) return 'Unknown';
    return userObj.full_name || userObj.email || 'Unknown User';
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') return 'Admin';
    if (role === 'manager') return 'Manager';
    return 'Tech';
  };

  return (
    <div className="messages-container">
      <div className="messages-sidebar">
        <div className="messages-header">
          <h2>Messages</h2>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </div>
        
        <button 
          className="btn-new-message"
          onClick={() => {
            setShowNewMessage(true);
            setSelectedConversation(null);
            setSelectedRecipient(null);
            setMessages([]);
            setRecipientDropdownOpen(false);
          }}
        >
          + New Message
        </button>

        {loading ? (
          <div className="loading-state">Loading conversations...</div>
        ) : showNewMessage ? (
          <div className="new-message-users" ref={recipientDropdownRef}>
            <div className="new-message-header">
              <h3>Select Recipient</h3>
              <button type="button" className="btn-cancel-new" onClick={handleCancelNewMessage}>
                Cancel
              </button>
            </div>
            {messageableUsers.length === 0 ? (
              <div className="empty-state">
                <p>No recipients available</p>
                <p className="empty-hint">
                  {user?.user_metadata?.role === 'user' 
                    ? 'Techs can only message managers and admins. Please contact an administrator if you need to send a message.'
                    : 'No other users found in the system.'}
                </p>
              </div>
            ) : (
              <div className="recipient-dropdown">
                <button
                  type="button"
                  className={`recipient-dropdown-trigger ${!selectedRecipient ? 'placeholder' : ''}`}
                  onClick={() => setRecipientDropdownOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={recipientDropdownOpen}
                >
                  <span className="recipient-trigger-label">
                    {selectedRecipient
                      ? getDisplayName(selectedRecipient)
                      : 'Choose a recipient...'}
                  </span>
                  <span className="recipient-trigger-arrow">{recipientDropdownOpen ? '▲' : '▼'}</span>
                </button>
                {recipientDropdownOpen && (
                  <ul className="recipient-dropdown-menu" role="listbox">
                    {messageableUsers.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className={`recipient-dropdown-item ${selectedRecipient?.id === u.id ? 'selected' : ''}`}
                          onClick={() => {
                            handleStartNewConversation(u);
                            setRecipientDropdownOpen(false);
                          }}
                          role="option"
                          aria-selected={selectedRecipient?.id === u.id}
                        >
                          <span className="user-name">{getDisplayName(u)}</span>
                          <span className="user-role">{getRoleBadge(u.role)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="empty-state">No conversations yet</div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.userId}
                  className={`conversation-item ${selectedConversation?.userId === conv.userId ? 'active' : ''}`}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <div className="conversation-info">
                    <div className="conversation-header">
                      <div className="conversation-name">{getDisplayName(conv.user)}</div>
                      {conv.unreadCount > 0 && (
                        <span className="unread-count">{conv.unreadCount}</span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <div className="conversation-preview">
                        <span className="preview-text">
                          {conv.lastMessage.sender_id === user?.id ? 'You: ' : ''}
                          {conv.lastMessage.message}
                        </span>
                        <span className="preview-time">
                          {new Date(conv.lastMessage.created_at).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div className="messages-main">
        {selectedConversation || selectedRecipient ? (
          <>
            <div className="conversation-header-bar">
              <div className="conversation-user-info">
                <h3>{getDisplayName(selectedConversation?.user || selectedRecipient)}</h3>
                <span className="user-role-badge">
                  {getRoleBadge((selectedConversation?.user || selectedRecipient)?.role)}
                </span>
              </div>
            </div>

            <div className="messages-list">
              {messages.length === 0 ? (
                <div className="empty-messages">No messages yet. Start the conversation!</div>
              ) : (
                messages.map((msg) => {
                  const isOwnMessage = msg.sender_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`message-item ${isOwnMessage ? 'own-message' : 'other-message'}`}
                    >
                      <div className="message-content">
                        <div className="message-text">{msg.message}</div>
                        <div className="message-time">
                          {new Date(msg.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendMessage} className="message-input-form">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows="3"
                className="message-input"
                disabled={sending}
              />
              <button type="submit" className="btn-send" disabled={!newMessage.trim() || sending}>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </form>
          </>
        ) : (
          <div className="no-conversation-selected">
            <p>Select a conversation or start a new message</p>
          </div>
        )}
      </div>
    </div>
  );
}
